import React, { useState, useEffect, useRef } from "react";
import Icon from "../components/Icon";
import Reveal from "../components/Reveal";
import HeroBackdrop from "../components/HeroBackdrop";
import Button from "../components/Button";
import { LEGAL_SECTIONS, LAST_UPDATED } from "./legalContent";

/* ------------------------------------------------------------------ */
/*  Scroll spy — highlights the section currently being read            */
/* ------------------------------------------------------------------ */

function useScrollSpy(ids) {
  const [active, setActive] = useState(ids[0]);
  const ratios = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.current[entry.target.id] = entry.isIntersecting
            ? entry.intersectionRatio
            : 0;
        });

        // Whichever tracked heading is most visible wins; ties fall to the
        // one earliest in the document.
        let best = null;
        let bestRatio = 0;
        ids.forEach((id) => {
          const ratio = ratios.current[id] || 0;
          if (ratio > bestRatio) {
            best = id;
            bestRatio = ratio;
          }
        });

        if (best) setActive(best);
      },
      {
        rootMargin: "-96px 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );

    ids.forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [ids]);

  return active;
}

/* ------------------------------------------------------------------ */
/*  Block renderers                                                     */
/* ------------------------------------------------------------------ */

const CALLOUT_TONES = {
  info: {
    wrap: "border-[#D8B4FE] bg-[#F8F1FF]",
    icon: "bg-[#6A0DAD] text-white",
    title: "text-[#4C087D]",
    text: "text-[#4D4B46]"
  },
  warning: {
    wrap: "border-[#FED7AA] bg-[#FFF7ED]",
    icon: "bg-[#C2410C] text-white",
    title: "text-[#9A3412]",
    text: "text-[#9A3412]"
  },
  neutral: {
    wrap: "border-[#E7DDCF] bg-[#F7F3EC]",
    icon: "bg-[#7A6F61] text-white",
    title: "text-[#1E1B16]",
    text: "text-[#4D4B46]"
  }
};

function Callout({ tone = "neutral", icon, title, text }) {
  const styles = CALLOUT_TONES[tone] || CALLOUT_TONES.neutral;

  return (
    <div className={`flex gap-4 rounded-2xl border p-6 ${styles.wrap}`}>
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${styles.icon}`}
      >
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <div>
        {title && (
          <h4 className={`text-[17px] font-semibold ${styles.title}`}>{title}</h4>
        )}
        <p className={`${title ? "mt-2" : ""} text-[15px] leading-relaxed ${styles.text}`}>
          {text}
        </p>
      </div>
    </div>
  );
}

function SplitRules({ permittedTitle, permitted, prohibitedTitle, prohibited }) {
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <div className="rounded-2xl border border-[#C7E9D2] bg-[#EDF9F0] p-6">
        <h4 className="flex items-center gap-2.5 text-[17px] font-semibold text-[#166534]">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1B8F4B] text-white">
            <Icon name="check" className="h-4 w-4" />
          </span>
          {permittedTitle}
        </h4>
        <ul className="mt-4 space-y-2.5">
          {permitted.map((item) => (
            <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-[#1B5E3A]">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1B8F4B]" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border border-[#F4C7C7] bg-[#FFF1F1] p-6">
        <h4 className="flex items-center gap-2.5 text-[17px] font-semibold text-[#9F1239]">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#D94444] text-white">
            <Icon name="close" className="h-4 w-4" />
          </span>
          {prohibitedTitle}
        </h4>
        <ul className="mt-4 space-y-2.5">
          {prohibited.map((item) => (
            <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-[#8B2635]">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D94444]" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Block({ block }) {
  if (block.p) {
    return <p className="text-[16px] leading-[1.75] text-[#4D4B46]">{block.p}</p>;
  }

  if (block.h3) {
    return (
      <h3 className="pt-2 text-[19px] font-semibold tracking-tight text-[#1E1B16]">
        {block.h3}
      </h3>
    );
  }

  if (block.ul) {
    return (
      <ul className="space-y-3">
        {block.ul.map((item) => (
          <li key={item} className="flex gap-3.5 text-[16px] leading-[1.75] text-[#4D4B46]">
            <span
              className="mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#D8B4FE]"
              aria-hidden="true"
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (block.callout) return <Callout {...block.callout} />;
  if (block.split) return <SplitRules {...block.split} />;

  return null;
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

const SECTION_IDS = LEGAL_SECTIONS.map((section) => section.id);

function PrivacyPolicy() {
  const active = useScrollSpy(SECTION_IDS);
  const [tocOpen, setTocOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 900);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setTocOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-[#FAF8F2] text-[#1E1B16]">
      {/* ============================== HERO ============================== */}
      <section className="relative isolate overflow-hidden bg-[#220339] print:hidden">
        <HeroBackdrop tone="hero" foot />

        <div className="relative mx-auto max-w-4xl px-6 pb-28 pt-32 text-center sm:pt-36 md:px-10 lg:pt-40">
          <Reveal>
            <span className="mw-eyebrow text-[#D8B4FE]">Legal</span>
            <h1 className="mw-display mt-5 text-white">Privacy Policy</h1>
            <p className="mw-lede mx-auto mt-6 max-w-2xl text-[#DCC9F2]">
              MONDOWAY Digital Platform — the terms that govern your use of the app,
              together with our privacy and location data policies.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium text-white backdrop-blur">
                <Icon name="clock" className="h-4 w-4 text-[#D8B4FE]" />
                Last updated: {LAST_UPDATED}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium text-white backdrop-blur">
                <Icon name="list" className="h-4 w-4 text-[#D8B4FE]" />
                {LEGAL_SECTIONS.length} sections
              </span>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <Button
              variant="glass"
              size="md"
              icon="arrow"
              className="mt-8"
              onClick={() => window.print()}
            >
              Print or save as PDF
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ======================= MOBILE TABLE OF CONTENTS ================= */}
      <div className="sticky top-[68px] z-30 border-b border-[#EAE2D6] bg-[#FAF8F2]/90 backdrop-blur-xl lg:hidden print:hidden">
        <button
          onClick={() => setTocOpen((open) => !open)}
          aria-expanded={tocOpen}
          className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
        >
          <span className="flex min-w-0 items-center gap-3">
            <Icon name="list" className="h-5 w-5 shrink-0 text-[#6A0DAD]" />
            <span className="truncate text-[15px] font-semibold text-[#1E1B16]">
              Jump to section
            </span>
          </span>
          <Icon
            name="chevron"
            className={`h-5 w-5 shrink-0 text-[#6A0DAD] transition-transform duration-300 ${
              tocOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {tocOpen && (
          <nav aria-label="Table of contents" className="max-h-[60vh] overflow-y-auto px-4 pb-4">
            <ol className="space-y-1">
              {LEGAL_SECTIONS.map((section, index) => (
                <li key={section.id}>
                  <button
                    onClick={() => goToSection(section.id)}
                    className={`flex w-full gap-3 rounded-xl px-4 py-3 text-left text-[15px] transition-colors duration-200 ${
                      active === section.id
                        ? "bg-[#F4E8FF] font-semibold text-[#6A0DAD]"
                        : "text-[#4D4B46] hover:bg-[#F4E8FF]/60"
                    }`}
                  >
                    <span className="tabular-nums text-[#9A9085]">{index + 1}.</span>
                    {section.title}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        )}
      </div>

      {/* ============================ DOCUMENT ============================ */}
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Desktop table of contents */}
          <aside className="hidden lg:col-span-4 lg:block print:hidden">
            <nav
              aria-label="Table of contents"
              className="mw-glass sticky top-[104px] rounded-2xl p-5"
            >
              <h2 className="mw-eyebrow px-3 text-[#6A0DAD]">On this page</h2>
              <ol className="mt-4 max-h-[calc(100vh-14rem)] space-y-0.5 overflow-y-auto">
                {LEGAL_SECTIONS.map((section, index) => (
                  <li key={section.id}>
                    <button
                      onClick={() => goToSection(section.id)}
                      aria-current={active === section.id ? "true" : undefined}
                      className={`flex w-full gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] leading-snug transition-all duration-200 ${
                        active === section.id
                          ? "bg-[#F4E8FF] font-semibold text-[#6A0DAD]"
                          : "text-[#4D4B46] hover:bg-[#F4E8FF]/60 hover:text-[#6A0DAD]"
                      }`}
                    >
                      <span className="tabular-nums text-[#9A9085]">{index + 1}.</span>
                      <span>{section.title}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          {/* Sections */}
          <main className="lg:col-span-8">
            <div className="space-y-5">
              {LEGAL_SECTIONS.map((section, index) => (
                <section
                  key={section.id}
                  aria-labelledby={section.id}
                  className="mw-glass scroll-mt-[104px] rounded-[1.5rem] p-7 transition-shadow duration-500 hover:shadow-[0_30px_60px_-32px_rgba(106,13,173,0.4)] sm:p-9 print:border-0 print:bg-transparent print:p-0 print:shadow-none print:backdrop-filter-none"
                >
                  <h2
                    id={section.id}
                    className="flex scroll-mt-[104px] items-start gap-4 text-2xl font-bold tracking-tight text-[#1E1B16]"
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#6A0DAD] to-[#4C087D] text-sm font-bold text-white shadow-lg shadow-[#6A0DAD]/25">
                      {index + 1}
                    </span>
                    {section.title}
                  </h2>

                  <div className="mt-7 space-y-5 pl-0 sm:pl-13">
                    {section.blocks.map((block, blockIndex) => (
                      <Block key={blockIndex} block={block} />
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Acknowledgement footer */}
            <div className="mt-5 overflow-hidden rounded-[1.5rem] bg-[linear-gradient(150deg,#4C087D_0%,#6A0DAD_55%,#3B0764_100%)] p-9 text-center text-white shadow-[0_30px_60px_-28px_rgba(106,13,173,0.55)] print:hidden">
              <div className="relative">
                <p className="text-lg font-semibold">
                  © 2026 MONDOWAY. All rights reserved.
                </p>
                <p className="mt-2 text-[15px] text-[#E9D5FF]">
                  By using our application, you agree to these terms and conditions.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={`fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-xl border border-[#EAE2D6] bg-white text-[#6A0DAD] shadow-xl shadow-[#1E1B16]/10 transition-all duration-300 hover:border-[#D8B4FE] hover:bg-[#F4E8FF] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20 print:hidden ${
          showTop ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
      >
        <Icon name="chevron" className="h-5 w-5 rotate-180" />
      </button>
    </div>
  );
}

export default PrivacyPolicy;
